/* global google */
import { useRef, useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { MapPin, Search, Navigation, Loader2, CheckCircle, AlertCircle, X } from 'lucide-react';
import {
  setTemporaryCoordinates,
  resetTemporaryCoordinates,
  setCoordinates,
  setLocationName,
} from '../../slices/locationSlice';
import { getUserCoordinates, reverseGeocode } from '../../utils/geolocation';
import { cn } from '../../lib/utils';

/**
 * LocationDropdown - Location search dropdown for header
 */
const LocationDropdown = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  
  const temporaryLocation = useSelector((state) => state.location.temporaryCoordinates);
  const tempLocationName = useSelector((state) => state.location.tempLocationName);
  
  const [isLoading, setIsLoading] = useState(false);
  const [isGettingCurrentLocation, setIsGettingCurrentLocation] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  const isClickOnAutocomplete = useCallback((target) => {
    const pacContainers = document.querySelectorAll('.pac-container');
    for (const container of pacContainers) {
      if (container.contains(target)) return true;
    }
    return target.closest('.pac-container') || target.closest('.pac-item');
  }, []);

  // Combined effect: scroll lock, focus, and event listeners
  useEffect(() => {
    if (!isOpen) return;

    // Lock scroll
    const originalBodyOverflow = document.body.style.overflow;
    const originalHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    // Focus input after animation
    const focusTimer = setTimeout(() => {
      inputRef.current?.focus();
    }, 150);

    // Click outside handler
    const handleClickOutside = (event) => {
      if (dropdownRef.current?.contains(event.target)) return;
      if (isClickOnAutocomplete(event.target)) return;
      onClose();
    };

    // Escape key handler
    const handleEscape = (event) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('click', handleClickOutside, true);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = originalBodyOverflow || '';
      document.documentElement.style.overflow = originalHtmlOverflow || '';
      clearTimeout(focusTimer);
      document.removeEventListener('click', handleClickOutside, true);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose, isClickOnAutocomplete]);

  // Google Places Autocomplete initialization
  useEffect(() => {
    if (!isOpen || !window.google || !inputRef.current) return;
    
    if (autocompleteRef.current) return;

    const autocomplete = new google.maps.places.Autocomplete(inputRef.current, {
      types: ['(regions)'],
    });
    autocompleteRef.current = autocomplete;

    autocomplete.addListener('place_changed', async () => {
      setIsLoading(true);
      setMessage({ text: '', type: '' });

      try {
        const place = autocomplete.getPlace();

        if (!place.geometry) {
          setMessage({
            text: t('placeDetailsNotFound', 'Place details not found'),
            type: 'error'
          });
          setIsLoading(false);
          return;
        }

        const location = place.geometry.location;
        const cityName = place.formatted_address;

        dispatch(
          setTemporaryCoordinates({
            coordinates: [location.lng(), location.lat()],
            tempLocationName: cityName,
          })
        );

        if (inputRef.current) {
          inputRef.current.value = '';
          inputRef.current.blur();
        }

        setMessage({
          text: t('locationSetTo', 'Location set to') + ` ${cityName}`,
          type: 'success'
        });

        setTimeout(() => {
          onClose();
          setMessage({ text: '', type: '' });
        }, 1500);

      } catch (error) {
        console.error('Error setting location:', error);
        setMessage({
          text: t('locationSetError', 'Error setting location'),
          type: 'error'
        });
      } finally {
        setIsLoading(false);
      }
    });

    return () => {
      // remove autocomplete listeners
      if (autocompleteRef.current) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
    };
  }, [isOpen, dispatch, t, onClose]);

  // Use current location (fetches fresh GPS)
  const handleUseCurrentLocation = async () => {
    setIsGettingCurrentLocation(true);
    setMessage({ text: '', type: '' });

    try {
      const { coordinates, source } = await getUserCoordinates();
      const [lng, lat] = coordinates;
      const locationName = await reverseGeocode(lat, lng);

      dispatch(resetTemporaryCoordinates());
      dispatch(setCoordinates({ coordinates, source }));
      dispatch(setLocationName(locationName));

      setMessage({
        text: t('locationUpdated', 'Using your current location'),
        type: 'success'
      });

      setTimeout(() => {
        onClose();
        setMessage({ text: '', type: '' });
      }, 1500);

    } catch (error) {
      console.error('Error getting current location:', error);
      setMessage({
        text: t('locationError', 'Could not get your location'),
        type: 'error'
      });
    } finally {
      setIsGettingCurrentLocation(false);
    }
  };

  // Clear temporary location
  const handleResetLocation = () => {
    dispatch(resetTemporaryCoordinates());
    
    if (inputRef.current) {
      inputRef.current.value = '';
    }

    setMessage({
      text: t('locationReset', 'Reset to your current location'),
      type: 'success'
    });

    setTimeout(() => {
      onClose();
      setMessage({ text: '', type: '' });
    }, 1500);
  };

  // Backdrop click handler
  const handleBackdropClick = (event) => {
    if (isClickOnAutocomplete(event.target)) return;
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div 
        className="fixed inset-0 z-40 bg-black/20"
        onClick={handleBackdropClick}
      />
      
      <div
        className="fixed left-0 right-0 top-[90px] sm:top-[30px] z-50 px-4 flex justify-center sm:justify-start sm:absolute sm:top-full sm:mt-2 sm:px-0 sm:left-0 sm:right-auto"
      >
        <div
          ref={dropdownRef}
          className={cn(
            "bg-white rounded-xl shadow-lg",
            "border border-gray-200",
            "animate-in fade-in slide-in-from-top-2 duration-200",
            "w-full max-w-[320px] sm:w-80"
          )}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 pt-4 pb-2">
            <h3 className="font-semibold text-base text-gray-900">
              {t('changeLocation', 'Change Location')}
            </h3>
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              aria-label={t('close', 'Close')}
            >
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="px-4 pb-4 space-y-3">
            {/* Current Temporary Location indicator */}
            {temporaryLocation && tempLocationName && (
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg">
                <MapPin className="w-4 h-4 text-blue-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] text-blue-600 font-medium uppercase tracking-wide">
                    {t('viewingFrom', 'Viewing from')}
                  </p>
                  <p className="text-sm font-medium text-blue-900 truncate">
                    {tempLocationName}
                  </p>
                </div>
              </div>
            )}

            {/* Search Input */}
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                {isLoading ? (
                  <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
                ) : (
                  <Search className="w-4 h-4 text-gray-400" />
                )}
              </div>
              <input
                ref={inputRef}
                type="text"
                placeholder={t('searchCityOrZip', 'Enter city or zip code...')}
                className={cn(
                  "w-full pl-10 pr-4 py-3",
                  "text-sm border border-gray-200 rounded-xl",
                  "bg-white text-gray-900 placeholder-gray-400",
                  "focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20",
                  "transition-all duration-200"
                )}
                disabled={isLoading}
              />
            </div>

            {/* Use Current Location Button */}
            <button
              onClick={handleUseCurrentLocation}
              disabled={isGettingCurrentLocation || isLoading}
              className={cn(
                "w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl",
                "font-medium text-sm transition-all duration-200",
                "bg-gray-50 text-gray-700 hover:bg-gray-100 active:scale-[0.98]",
                "border border-gray-200",
                "disabled:opacity-50 disabled:cursor-not-allowed"
              )}
            >
              {isGettingCurrentLocation ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{t('gettingLocation', 'Getting location...')}</span>
                </>
              ) : (
                <>
                  <Navigation className="w-4 h-4" />
                  <span>{t('useCurrentLocation', 'Use My Current Location')}</span>
                </>
              )}
            </button>

            {/* Reset button */}
            {temporaryLocation && (
              <button
                onClick={handleResetLocation}
                disabled={isLoading}
                className={cn(
                  "w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl",
                  "font-semibold text-sm transition-all duration-200",
                  "bg-gray-900 text-white hover:bg-gray-800 active:scale-[0.98]",
                  "disabled:opacity-50 disabled:cursor-not-allowed"
                )}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>{t('resetting', 'Resetting...')}</span>
                  </>
                ) : (
                  <span>{t('clearTempLocation', 'Clear Temporary Location')}</span>
                )}
              </button>
            )}

            {/* Message */}
            {message.text && (
              <div className={cn(
                "flex items-center gap-2 p-3 rounded-lg",
                message.type === 'success' ? "bg-green-50" : "bg-red-50"
              )}>
                {message.type === 'success' ? (
                  <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                )}
                <p className={cn(
                  "text-sm font-medium",
                  message.type === 'success' ? "text-green-800" : "text-red-800"
                )}>
                  {message.text}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default LocationDropdown;
